/**
 * React context exposing wizard navigation helpers and state to descendants
 * of <Wizard>. Use {@link useWizardContext} in step body components to
 * access `next` / `back` / `goTo` / `close` and metadata like the current
 * step index and `isSubmitting`.
 *
 * CTA callbacks (`primaryCta.onClick`, `secondaryCtas[].onClick`) receive
 * the same context as a function argument — they can't call hooks because
 * they aren't React components.
 */

import { createContext, useContext } from 'react';

import type { WizardCtx } from './types';

const WizardContext = createContext<WizardCtx | null>(null);

export const WizardProvider = WizardContext.Provider;

// eslint-disable-next-line react-refresh/only-export-components -- standard context pattern: context co-located with its provider
export function useWizardContext(): WizardCtx {
  const ctx = useContext(WizardContext);
  if (!ctx) {
    throw new Error('useWizardContext must be used within a <Wizard>');
  }
  return ctx;
}
